/**
 * Minimal RFC 5545 calendar file for one event (forms engine, FORMS_DESIGN §7.3). Pure: times are
 * written in UTC (`Z` form), so no VTIMEZONE is needed and DST is the caller's clock's business.
 * Text values are escaped (`\\ \; \, \n`) and lines folded at 75 octets (UTF-8 aware, never
 * splitting a multi-byte character).
 */

export interface IcsEvent {
  /** Stable unique id, e.g. `<formId>@growhero`. */
  uid: string;
  /** When the file was generated. */
  stamp: Date;
  start: Date;
  /** Defaults to start + 2 hours when absent. */
  end?: Date | null;
  summary: string;
  location?: string | null;
  description?: string | null;
  url?: string | null;
  /** Bumped on every logistics change / cancellation so calendars replace the entry. */
  sequence?: number;
  cancelled?: boolean;
  /** Organizer display name (no address: we never expose one). */
  organizerName?: string | null;
}

/** `20261014T153000Z` */
export function icsDate(d: Date): string {
  return d
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

/** RFC 5545 TEXT escaping. */
export function icsEscape(value: string): string {
  return value
    .replace(/\r\n|\r/g, '\n')
    .replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

const encoder = new TextEncoder();

/** Fold a content line at 75 octets; continuation lines start with one space. */
export function icsFold(line: string): string {
  if (encoder.encode(line).length <= 75) return line;
  const parts: string[] = [];
  let current = '';
  let bytes = 0;
  // The first line may hold 75 octets, continuation lines 74 (+1 for the leading space).
  let limit = 75;
  for (const ch of line) {
    const n = encoder.encode(ch).length;
    if (bytes + n > limit) {
      parts.push(current);
      current = '';
      bytes = 0;
      limit = 74;
    }
    current += ch;
    bytes += n;
  }
  if (current) parts.push(current);
  return parts.join('\r\n ');
}

export function buildIcs(e: IcsEvent): string {
  const end = e.end ?? new Date(e.start.getTime() + 2 * 3_600_000);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//GrowHero//Forms//EL',
    'CALSCALE:GREGORIAN',
    // PUBLISH even when cancelled: a downloaded file has no organizer (iTIP CANCEL needs one);
    // STATUS:CANCELLED + a higher SEQUENCE replaces the entry in calendar apps.
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${icsEscape(e.uid)}`,
    `DTSTAMP:${icsDate(e.stamp)}`,
    `DTSTART:${icsDate(e.start)}`,
    `DTEND:${icsDate(end)}`,
    `SEQUENCE:${Math.max(0, Math.floor(e.sequence ?? 0))}`,
    `STATUS:${e.cancelled ? 'CANCELLED' : 'CONFIRMED'}`,
    `SUMMARY:${icsEscape(e.summary)}`,
  ];
  if (e.location) lines.push(`LOCATION:${icsEscape(e.location)}`);
  if (e.description) lines.push(`DESCRIPTION:${icsEscape(e.description)}`);
  if (e.url) lines.push(`URL:${e.url.replace(/[\r\n]/g, '')}`);
  if (e.organizerName) lines.push(`X-WR-CALNAME:${icsEscape(e.organizerName)}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return `${lines.map(icsFold).join('\r\n')}\r\n`;
}

/** Google Calendar "add event" link (built client-side; no API key). */
export function googleCalendarUrl(e: {
  title: string;
  start: Date;
  end?: Date | null;
  location?: string | null;
  details?: string | null;
}): string {
  const end = e.end ?? new Date(e.start.getTime() + 2 * 3_600_000);
  const q = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates: `${icsDate(e.start)}/${icsDate(end)}`,
  });
  if (e.location) q.set('location', e.location);
  if (e.details) q.set('details', e.details);
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}
